import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  Button,
  Paper,
  Typography,
} from "@mui/material";

import { SECTION } from "@/constants/examConstants";
import { RichTextDisplay } from "@/components/common";
import { formatExamTime } from "@/utils/formatExamTime";

import QuestionImage from "./QuestionImage";
import QuestionItem from "./QuestionItem";

/**
 * Shared rendering shell for an active question group inside an exam flow.
 *
 * Contains everything that lives between the part instructions and the
 * navigation bar: the title row (exam title + part number + Reading timer
 * chip + Submit button), and the side-by-side image/content/questions layout.
 *
 * Everything that varies between flows (banners above the title, what to do
 * on answer-select, whether the timer is visible at all) is controlled by
 * props or rendered by the caller in `headerExtras` / `topSlot`.
 */
const ExamQuestionView = ({
  // identity
  examTitle,
  currentPart,
  currentGroup,
  currentSection,
  // answers
  answers,
  onAnswerSelect,
  markedQuestions = {},
  onToggleMark,
  // timer + submit (Reading section / last question)
  showTimer = false,
  timeLeft = 0,
  showSubmit = false,
  isSubmitting = false,
  onSubmit,
  submitLabel = "Submit Exam",
  // optional banners
  topSlot = null,
  headerExtras = null,
}) => (
  <Container maxWidth="xl" sx={{ py: 3 }}>
    {topSlot}
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>
            {examTitle}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Part {currentPart?.partNumber}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {headerExtras}
          {showTimer && (
            <Chip
              label={formatExamTime(timeLeft)}
              color="primary"
              variant="filled"
              sx={{
                px: 1,
                py: 2.5,
                fontSize: "1rem",
                fontWeight: "bold",
                "& .MuiChip-label": { px: 2 },
              }}
            />
          )}
          {showSubmit &&
            (isSubmitting ? (
              <CircularProgress />
            ) : (
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={onSubmit}
                sx={{ px: 3, py: 1 }}
              >
                {submitLabel}
              </Button>
            ))}
        </Box>
      </Box>

      <Box sx={{ p: 1, borderRadius: 2 }}>
        {currentGroup && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
              width: "100%",
              alignItems: { xs: "center", md: "flex-start" },
            }}
          >
            {currentGroup.imageUrl && (
              <QuestionImage
                imageUrl={currentGroup.imageUrl}
                extraImageUrl={currentGroup.extraImageUrl || null}
              />
            )}
            {currentGroup.content && (
              <Box
                sx={{
                  width: { xs: "100%", md: "55%" },
                  position: { md: "sticky" },
                  top: { md: "20px" },
                  alignSelf: "flex-start",
                  mb: { xs: 3, md: 0 },
                }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                    maxHeight: "90vh",
                    overflow: "auto",
                  }}
                >
                  <RichTextDisplay
                    content={currentGroup.content}
                    userSelect={false}
                  />
                </Paper>
              </Box>
            )}
            <Box
              sx={{
                width:
                  currentGroup.imageUrl || currentGroup.content
                    ? { xs: "100%", md: "45%" }
                    : "100%",
              }}
            >
              {(currentGroup.questions || []).map((question, index) => (
                <QuestionItem
                  key={question._id}
                  index={index}
                  question={question}
                  answers={answers}
                  handleAnswerSelect={onAnswerSelect}
                  currentSection={currentSection}
                  isMarked={!!markedQuestions[question._id]}
                  onToggleMark={
                    currentSection === SECTION.READING
                      ? onToggleMark
                      : undefined
                  }
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  </Container>
);

export default ExamQuestionView;
